import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import DOMPurify from 'dompurify';
import { MarkdownStyles } from '../../../../../../../../../elements/ChatBox/MarkdownStyles';
import { ContentWrapper } from './AgentMessageContent.styled';
import { useTypewriterEffect } from '../../../../../../hooks';

/**
 * Normalize message content to string (handles API object/array formats).
 */
function normalizeContent(content) {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    const textItems = content.filter((item) => item?.type === 'text' && item?.text);
    if (textItems.length > 0) return textItems.map((item) => item.text).join('\n\n');
    const fallback = content.find((item) => item?.text || item?.content || item?.message);
    return fallback ? fallback.text || fallback.content || fallback.message : '';
  }
  if (content && typeof content === 'object') {
    return content.message || content.content || content.text || content.formattedText || '';
  }
  return '';
}

function isHTML(str) {
  if (typeof str !== 'string' || !str.trim()) return false;
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
}

const createMarkup = (content) => ({
  __html: DOMPurify.sanitize(content, {
    ALLOWED_ATTR: ['class', 'id', 'style', 'href', 'target', 'rel', 'src', 'alt', 'title'],
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'img',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
      'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'del',
    ],
    ALLOWED_CSS_PROPERTIES: [
      'color', 'background-color', 'font-weight', 'font-style',
      'text-decoration', 'text-align', 'font-size',
    ],
  }),
});

/**
 * Shared markdown/rehype plugin configuration.
 */
const REMARK_PLUGINS = [
  remarkGfm,
];

const REHYPE_SANITIZE_OPTIONS = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'mark'],
  attributes: {
    ...(defaultSchema.attributes || {}),
    a: [...((defaultSchema.attributes && defaultSchema.attributes.a) || []), 'target', 'rel'],
    img: [...((defaultSchema.attributes && defaultSchema.attributes.img) || []), 'src', 'alt', 'title'],
    mark: ['className'],
    '*': [...((defaultSchema.attributes && defaultSchema.attributes['*']) || []), 'className'],
  },
};

const REHYPE_PLUGINS = [
  rehypeRaw,
  [rehypeSanitize, REHYPE_SANITIZE_OPTIONS],
];

const MARKDOWN_COMPONENTS = {
  table: ({ node, ...props }) => <table className="markdown-table" {...props} />,
  tr: ({ node, ...props }) => <tr className="markdown-tr" {...props} />,
  th: ({ node, ...props }) => <th className="markdown-th" {...props} />,
  td: ({ node, ...props }) => <td className="markdown-td" {...props} />,
  mark: ({ node, className, children, ...props }) => {
    const classStr =
      typeof className === "string"
        ? className
        : Array.isArray(className)
          ? (className || []).filter(Boolean).join(" ")
          : "";
    return (
      <mark className={classStr || undefined} {...props}>
        {children}
      </mark>
    );
  },
  a: ({ node, href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="gfm-link"
      {...props}
    >
      {children ?? href}
    </a>
  ),
};

/**
 * Renders markdown content using ReactMarkdown.
 */
const MarkdownRenderer = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={REMARK_PLUGINS}
    rehypePlugins={REHYPE_PLUGINS}
    components={MARKDOWN_COMPONENTS}
  >
    {text}
  </ReactMarkdown>
);

/**
 * Renders agent message content with markdown/HTML formatting.
 * When isStreaming is true, applies a human-like typewriter effect.
 * When highlightMarkdown is set (search in agent message), markdown is rendered with <mark> highlights preserved.
 */
const AgentMessageContent = ({ content, highlightMarkdown = null, isStreaming = false, onTypingStateChange }) => {
  const text = normalizeContent(content);

  const { displayedText, isTyping } = useTypewriterEffect(text, isStreaming);
  const textToRender = displayedText;
  const streamingClass = isTyping ? 'streaming-message' : '';

  useEffect(() => {
    onTypingStateChange?.(isTyping);
  }, [isTyping, onTypingStateChange]);

  // Search highlight mode with markdown preserved (agent messages)
  if (highlightMarkdown != null) {
    return (
      <>
        <MarkdownStyles />
        <ContentWrapper className={`markdown-content ${streamingClass}`}>
          <MarkdownRenderer text={highlightMarkdown} />
        </ContentWrapper>
      </>
    );
  }

  // Empty content
  if (!textToRender) {
    return (
      <ContentWrapper className={`markdown-content ${streamingClass}`}>
        {' '}
      </ContentWrapper>
    );
  }

  // HTML content
  if (isHTML(textToRender)) {
    return (
      <>
        <MarkdownStyles />
        <ContentWrapper
          className={`markdown-content html-content ${streamingClass}`}
          dangerouslySetInnerHTML={createMarkup(textToRender)}
          role="text"
        />
      </>
    );
  }

  // Markdown content (default)
  return (
    <>
      <MarkdownStyles />
      <ContentWrapper className={`markdown-content ${streamingClass}`}>
        <MarkdownRenderer text={textToRender} />
      </ContentWrapper>
    </>
  );
};

export default AgentMessageContent;
